import { Icon, Typography } from '@mui/material'
import Grid from '@mui/material/Grid'
import { QueryStatus } from '@tanstack/react-query'
import React, { ReactElement } from 'react'
import { useTranslation } from 'react-i18next'
import { LoadingPlaceholderContainer } from 'ui/atoms'

type DataType = undefined | unknown
type NonUndefined<T> = T extends undefined ? never : T
type DataTypeObject<T extends Record<string, DataType>> = {
  [K in keyof T]: T[K]
}

type NonUndefinedDataTypeObject<T extends Record<string, DataType>> = {
  [K in keyof T]: NonUndefined<T[K]>
}

interface StatusComponentProps<Rest extends Record<string, unknown>, Data extends Record<string, unknown>> {
  data: DataTypeObject<Data>
  status: QueryStatus | QueryStatus[]
  LoadingFallback?: React.JSX.Element
  ErrorFallback?: React.JSX.Element
  Component: any
  ComponentProps?: Rest
  isFetching?: boolean
}

export default function StatusComponent<Rest extends Record<string, unknown>, Data extends Record<string, unknown>>({
  status,
  data,
  LoadingFallback,
  ErrorFallback,
  Component,
  ComponentProps,
  isFetching,
}: StatusComponentProps<Rest, Data>): ReactElement {
  const { t } = useTranslation()

  if (Object.keys(data).every((key) => data[key] !== undefined)) {
    return (
      <Component
        {...(data as NonUndefinedDataTypeObject<Data>)}
        {...(ComponentProps && typeof ComponentProps === 'object' ? ComponentProps : {})}
        isFetching={isFetching}
      />
    )
  } else if (
    (Array.isArray(status) && status.some((status: QueryStatus) => status === `error`)) ||
    status === `error`
  ) {
    return (
      ErrorFallback ?? (
        <Grid container alignItems="center" justifyContent="center">
          <Icon className={`fal fa-exclamation-triangle`} style={{ color: `red` }} />
          <Typography variant="h4">{t(`Content failed to load`)}</Typography>
        </Grid>
      )
    )
  } else if (
    (Array.isArray(status) && status.some((status: QueryStatus) => status === `pending` || status === `success`)) ||
    status === `pending` ||
    status === `success`
  ) {
    // status can be success even when data is undefined in a few cases where it should still be loading
    return LoadingFallback ?? <LoadingPlaceholderContainer />
  }
  throw Error(`Status component got unrecognized status`)
}
